import { lazy, ReactNode, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import AuthPage from './components/Account/AuthPage';
import ProfilePage from './components/Account/ProfilePage';
import RewardsPage from './components/Account/RewardsPage';
import CheckoutPage from './components/CheckoutPage/CheckoutPage';
import CollectionPage from './components/Collections/CollectionPage';
import HomePage from './components/HomePage/HomePage';
import OrderDetailsPage from './components/Orders/OrderDetailsPage';
import OrdersPage from './components/Orders/OrdersPage';
import ProductDetailsPage from './components/ProductDetailsPage/ProductDetailsPage';
import RequireAuth from './components/RequireAuth/RequireAuth';
import SearchPage from './components/SearchPage/SearchPage';
import StorefrontLayout from './components/StorefrontLayout/StorefrontLayout';
import VerifyEmailPage from './components/VerifyEmailPage/VerifyEmailPage';
import StockAlertLinkPage from './components/StockAlerts/StockAlertLinkPage';
import WishlistPage from './components/Wishlist/WishlistPage';
import './App.css';
import StoriesPage from './components/Stories/StoriesPage';
import StoryPage from './components/Stories/StoryPage';

// Admin pages load on demand so shoppers never download them.
const AdminLoginPage = lazy(() => import('./components/Admin/AdminLoginPage'));
const AdminLayout = lazy(() => import('./components/Admin/AdminLayout'));
const AdminItemsPage = lazy(() => import('./components/Admin/AdminItemsPage'));
const AdminLeadsPage = lazy(() => import('./components/Admin/AdminLeadsPage'));
const AdminUsersPage = lazy(() => import('./components/Admin/AdminUsersPage'));
const AdminCommercePage = lazy(() => import('./components/Admin/AdminCommercePage'));

const admin = (page: ReactNode) => <Suspense fallback={<p role="status">Loading…</p>}>{page}</Suspense>;

export default function App() {
  return (
    <Routes>
      <Route element={<StorefrontLayout />}>
        <Route index element={<HomePage />} />
        <Route path="stories" element={<StoriesPage />} />
        <Route path="stories/:slug" element={<StoryPage />} />
        <Route path="collections/:slug" element={<CollectionPage />} />
        <Route path="products/:itemId" element={<ProductDetailsPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="login" element={<AuthPage />} />
        <Route path="checkout" element={<CheckoutPage />} />
        <Route path="wishlist" element={<WishlistPage />} />
        <Route path="verify-email" element={<VerifyEmailPage />} />
        <Route path="stock-alerts/confirm" element={<StockAlertLinkPage action="confirm" />} />
        <Route path="stock-alerts/unsubscribe" element={<StockAlertLinkPage action="unsubscribe" />} />
        <Route element={<RequireAuth />}>
          <Route path="profile" element={<ProfilePage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/:orderId" element={<OrderDetailsPage />} />
          <Route path="rewards" element={<RewardsPage />} />
        </Route>
        {/* Unknown storefront URLs keep the header and footer, as before. */}
        <Route path="*" element={null} />
      </Route>
      <Route path="admin/login" element={admin(<AdminLoginPage />)} />
      <Route path="admin" element={admin(<AdminLayout />)}>
        <Route index element={admin(<AdminItemsPage />)} />
        <Route path="items" element={admin(<AdminItemsPage />)} />
        <Route path="leads" element={admin(<AdminLeadsPage />)} />
        <Route path="users" element={admin(<AdminUsersPage />)} />
        <Route path="commerce" element={admin(<AdminCommercePage />)} />
      </Route>
    </Routes>
  );
}
